import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { useFormRegistration } from '@/data/hook/question/useFormRegistration';
import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import { PointsSection } from '../fields/PointsSection';

import { FeedbackCreateDTO } from '@/models/Feedback';
import { FlashcardQuestion } from '@/models/Question';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
  TranslatableDict,
} from '@/models/TranslatableField';
import { adapterFeedbacks } from '@/utils/questions/adapterFeedbacks';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import { QuestionFormContainer } from '@/components/question/forms/QuestionFormContainer';
import { QuestionTitleField } from '@/components/question/forms/fields/QuestionTitleField';
import { Toggle } from '@/components/ui/daisy';
import MyCkeditor from '@/components/editor/MyCkeditor';
import { ImageFolderEnum } from '@/models/CkEditor';
import Fieldset from '@/components/ui/daisy/fieldset';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import QuestionPreview from '@/components/question/forms/QuestionPreview';
import adapterFlashcardForm from '@/utils/questions/adapterFlashcardForm';
import { default as Editor } from 'ckeditor5-custom-build/build/ckeditor';

const alignEmptyEditorToCenter = (editor: typeof Editor) => {
  if (!editor.getData()) {
    editor.execute('alignment', { value: 'center' });
  }
};

export type FlashcardFormFields = {
  title: TranslatableDict;
  feedbacks: FeedbackCreateDTO[];
  frontContent: TranslatableDict;
  backContent: TranslatableDict;
  showFrontOnBack: boolean;
  maxGrade?: number;
};

type FlashcardFormProps = {
  isActive?: boolean;
  question: FlashcardQuestion;
  isLoading?: boolean;
  onSubmit: (data: FlashcardFormFields) => Promise<FlashcardQuestion>;
};

export default function FlashcardForm({
  question,
  isLoading,
  onSubmit,
  isActive = true,
}: FlashcardFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.flashcardForm',
  });

  const { identifier } = question;

  const [activeLanguage, setActiveLanguage] =
    useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const gradeCtx = useQuestionExamGradeCtx();
  const maxGradeDefault = gradeCtx?.getGradeForQuestion(question)?.maxGrade;

  const defaultValues = useMemo<FlashcardFormFields>(
    () => ({
      title: normalizeTranslatableField(question?.title),
      frontContent: normalizeTranslatableField(question?.frontContent),
      backContent: normalizeTranslatableField(question?.backContent),
      feedbacks: adapterFeedbacks(question?.feedbacks ?? []),
      showFrontOnBack: question?.showFrontOnBack ?? false,
      maxGrade: maxGradeDefault,
    }),
    [question, maxGradeDefault],
  );

  const { submitForm } = useQuestionFormsOrchestrator();

  const methods = useForm<FlashcardFormFields>({ defaultValues });
  const { watch, register, trigger, reset, formState, control } = methods;

  const realTimeValues = watch();
  const previewQuestion = adapterFlashcardForm(realTimeValues);

  const { hasChanges, handleReset } = useFormRegistration({
    identifier,
    formState,
    submit: onSubmit,
    validate: trigger,
    watch,
    reset,
    defaultValues,
  });

  return (
    <div className="flex gap-6">
      <QuestionFormContainer
        methods={methods}
        activeLanguage={activeLanguage}
        onLanguageChange={setActiveLanguage}
        onSubmit={e => {
          e.preventDefault();
          submitForm(identifier);
        }}
        isLoading={isLoading}
        hasChanges={hasChanges}
        onCancel={handleReset}
      >
        <div className="flex flex-col gap-4">
          <QuestionTitleField
            activeLanguage={activeLanguage}
            placeholder={t('titlePlaceholder')}
            hasTitleDescription={false}
            labelTitle={t('labelTitle')}
            requiredMessage={t('titleRequired')}
          />

          <Fieldset
            label={t('frontContent')}
            labelAlt={formState.errors?.frontContent?.[activeLanguage]?.message}
          >
            <Fieldset.Label>{t('contextDescription')}</Fieldset.Label>
            <ConditionalRenderer condition={isActive}>
              <Controller
                key={activeLanguage}
                name={`frontContent.${activeLanguage}`}
                control={control}
                rules={{ required: t('frontContentRequired') }}
                render={({ field: { value, onChange } }) => (
                  <MyCkeditor
                    key={activeLanguage}
                    content={value}
                    folder={ImageFolderEnum.ACTIVITY}
                    handleContentChange={onChange}
                    placeholder={t('frontContentPlaceholder')}
                    onEditorReady={alignEmptyEditorToCenter}
                  />
                )}
              />
            </ConditionalRenderer>
          </Fieldset>

          <label className="flex items-center gap-2">
            <span className="text-sm">{t('showFrontOnBack')}</span>
            <Toggle color="primary" {...register('showFrontOnBack')} />
          </label>

          <Fieldset
            label={t('backContent')}
            labelAlt={formState.errors?.backContent?.[activeLanguage]?.message}
          >
            <Fieldset.Label>{t('contextDescription')}</Fieldset.Label>
            <ConditionalRenderer condition={isActive}>
              <Controller
                key={activeLanguage}
                name={`backContent.${activeLanguage}`}
                control={control}
                rules={{ required: t('backContentRequired') }}
                render={({ field: { value, onChange } }) => (
                  <MyCkeditor
                    key={activeLanguage}
                    content={value}
                    folder={ImageFolderEnum.ACTIVITY}
                    handleContentChange={onChange}
                    placeholder={t('backContentPlaceholder')}
                    onEditorReady={alignEmptyEditorToCenter}
                  />
                )}
              />
            </ConditionalRenderer>
          </Fieldset>
        </div>

        <PointsSection />
      </QuestionFormContainer>

      <div className="flex-1">
        <QuestionPreview question={previewQuestion} />
      </div>
    </div>
  );
}
